import { useEffect, useReducer, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { Checkbox } from "../../components/Checkbox";
import { telephoneNoMask } from "../../functions/mask";
import { secondsToHour } from "../../functions/util";
import { useToast } from "../../hooks/useToast";
import { ModalQueue } from "../../components/ModalQueue";
import { useLocation } from "react-router-dom";

type ImageType = {
  id: string;
  url: string;
};

type QueueConfigType = {
  id: string;
  able: boolean;
  limitPeople: number;
  limitTime: number;
  images: ImageType[];
  observation: string;
  useMenu: boolean;
  useLocation: boolean;
  useNinegridMenu: boolean;
  externalMenuLink: string;
};

type QueueType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  position: number;
  seconds: number;
  minutesOnQueue: string;
  finalized: string;
};

export function QueueFind() {
  const location = useLocation();
  const { pluginspace_id, phone } = useParams();
  const { registerAccess, getToken } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [queueConfig, setQueueConfig] = useState<QueueConfigType | null>(null);
  const [queue, setQueue] = useState<QueueType | null>(null);
  const [minutesOnQueue, setMinutesOnQueue] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const showAlertEnterQueue = useRef(true);

  const toastComponent = useToast();
  useEffect(() => {
    registerAccess("queue/phone");
    init();
  }, []);

  async function init() {
    setLoading(true);
    toastComponent.setToastPosition("0rem");
    await getConfig();
    await getQueueConfig(true);
    setLoading(false);
  }

  const createInterval = (queueItem: QueueType) => {
    setInterval(() => {
      queueItem.seconds++;
      queueItem.minutesOnQueue = secondsToHour(queueItem.seconds);
      setMinutesOnQueue(queueItem.minutesOnQueue);
    }, 1000);
  };

  async function getQueue(queueConfigId: number, doInterval: boolean) {
    await api
      .get(`/api/queueReservation/queue/${queueConfigId}/${phone}`, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const queueResponse = response.data;
        if (queueResponse && queueResponse.data && queueResponse.data.length) {
          const queueItem = queueResponse.data[queueResponse.data.length - 1];
          if (queueItem.finalized === "removed") {
            setAlertOpen(true);
            Swal.fire({
              icon: "warning",
              title: "Atenção!",
              text: "Oi! Percebemos que sua posição na fila foi alterada e você foi retirado. Em caso de dúvidas entre em contato com a gente. ",
            }).then(() => navigate(`/${pluginspace_id}/queue`));
          }
          if (doInterval) {
            const seconds =
              parseInt(queueItem.minutesOnQueue.split(":")[0]) * 3600 +
              parseInt(queueItem.minutesOnQueue.split(":")[1]) * 60 +
              parseInt(queueItem.minutesOnQueue.split(":")[2]);
            queueItem.seconds = seconds;
            createInterval(queueItem);
          }

          if (showAlertEnterQueue.current) {
            toastComponent.openToast("success", `Você entrou na fila.`, 3000);
            showAlertEnterQueue.current = false;
          }

          setQueue(queueItem);
          setAlertOpen(false);
        } else {
          setAlertOpen(true);
          if (Swal.getPopup() === null) {
            Swal.fire({
              icon: "error",
              title: "Posição não encontrada",
              text: "Não encontramos sua posição na fila. Por favor, verifique seus dados e tente novamente",
            }).then(() => navigate(`/${pluginspace_id}/queue`));
          }
        }
      })
      .catch((err) => {
        if (doInterval && Swal.getPopup() === null) {
          setAlertOpen(true);
          let title = "Posição não encontrada";
          let text =
            "Não encontramos sua posição na fila. Por favor, verifique seus dados e tente novamente";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          }).then(() => navigate(`/${pluginspace_id}/queue`));
        }
      });
  }

  async function getQueueConfig(doInterval: boolean) {
    await api
      .get("/api/queueReservation/queueConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const queueResponse = response.data;
        if (queueResponse && queueResponse.data && queueResponse.data[0]) {
          const queueConfigItem = queueResponse.data[0];
          if (queueConfigItem && queueConfigItem.able) {
            setQueueConfig(queueConfigItem);
          } else {
            Swal.fire({
              icon: "error",
              title: "Fila não está habilitada!",
              text: "Loja não está com o esquema de filas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Fila não configurada!",
            text: "Loja não está com o esquema de filas configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Fila não configurada!",
          text: "Loja não está com o esquema de filas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  async function quitQueue() {
    if (queue && queue.id) {
      setLoading(true);
      await api
        .put(
          `/api/queueReservation/queue/${queue.id}`,
          {
            finalized: "removed",
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          setLoading(false);
          const queueResponse = response.data;
          if (queueResponse.success) {
            Swal.fire({
              icon: "success",
              title: "Sucesso!",
              text: "Você saiu da fila com sucesso.",
            }).then(() => navigate(`/${pluginspace_id}/queue`));
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro ao sair da fila!",
              text: "Não foi possível sair da fila.",
            }).then(async () => await init());
          }
        })
        .catch((err) => {
          setLoading(false);
          let title = "Erro ao sair da fila!";
          let text = "Não foi possível sair da fila.";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          }).then(async () => await init());
        });
    } else {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Não foi possível encontrar alguém na fila com esse número de telefone.",
      }).then(() => navigate(`/${pluginspace_id}`));
    }
  }

  async function getConfig() {
    setConfig(await getAppConfig());
  }

  function navigateToMenu() {
    try {
      if (!queueConfig) return;

      if (queueConfig.useMenu && queueConfig.useNinegridMenu) {
        window.open(`/${pluginspace_id}/products`, "_blank");
      }

      if (
        queueConfig.useMenu &&
        !queueConfig.useNinegridMenu &&
        queueConfig.externalMenuLink
      ) {
        window.open(queueConfig.externalMenuLink, "_blank");
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (queueConfig) {
      // Chama a função getQueue com o parâmetro true
      getQueue(parseInt(queueConfig.id), true);

      // Define um timeout para chamar a função getQueue com o parâmetro false após 2000ms
      const timeoutId = setInterval(async () => {
        if (Swal.getPopup() === null)
          await getQueue(parseInt(queueConfig.id), false);
      }, 15000);

      // Retorna uma função de limpeza para cancelar o timeout quando o componente for desmontado
      return () => clearInterval(timeoutId);
    }
  }, [queueConfig]);

  return (
    <>
      <div className="queue-find">
        {loading || !minutesOnQueue ? (
          <div style={{ width: "100%", height: "calc(100vh)" }}>
            <Loading
              type="spinningBubbles"
              color="#FFF"
              width={64}
              height={64}
            />
          </div>
        ) : (
          <div className="content-queue-find">
            {queueConfig && (
              <div className="body-queue-find">
                {queueConfig.images && queueConfig.images[1] && (
                  <div className="queue-image">
                    <img src={queueConfig.images[1].url} alt="Imagem Fila 1" />
                  </div>
                )}
                <div className="queue-finded">
                  <h2>
                    {queue?.finalized === "confirmed"
                      ? "Chegou sua vez!"
                      : "Você está na fila de espera!"}
                  </h2>
                  {queue && (
                    <div className="queue-box">
                      <i className="fa fa-check-circle" aria-hidden="true"></i>
                      <div className="queue-info">
                        <b>{queue.name}</b>
                        <p style={{ color: "#6E7586" }}>{queue.phone}</p>
                        {!queue.finalized && (
                          <>
                            <p>
                              {queue.people}{" "}
                              {queue.people > 1 ? "pessoas" : "pessoa"}
                            </p>
                            <b>Posição: {queue.position}</b>
                            {minutesOnQueue && (
                              <p>
                                <b>Tempo de espera: </b>
                                {minutesOnQueue}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  {queue && !queue.finalized && (
                    <>
                      <p style={{ marginTop: "0.5em" }}>
                        Fique atento, você será chamado em breve. Indicamos que
                        não feche esta página pois você será notificado quando
                        chegar sua vez.
                      </p>
                      {queueConfig.observation && (
                        <div
                          className="observation-queue"
                          dangerouslySetInnerHTML={{
                            __html: queueConfig.observation,
                          }}
                        />
                      )}
                    </>
                  )}
                  <div className="buttons-queue-find">
                    {!queue?.finalized && (
                      <MainButton
                        color={
                          config?.webappcolor.webappbutton
                            ? config.webappcolor.webappbutton
                            : "#EAEAEA"
                        }
                        border={
                          config?.webappcolor.webappbutton
                            ? `1px solid ${config.webappcolor.webappbutton}`
                            : "1px solid #EAEAEA"
                        }
                        backgroundColor="transparent"
                        text="Sair da fila"
                        onClick={() =>
                          Swal.fire({
                            title: "Você tem certeza que deseja sair da fila?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#3085d6",
                            cancelButtonColor: "#d33",
                            confirmButtonText: "Sim, quero sair",
                            cancelButtonText: "Cancelar",
                          }).then((res) => {
                            if (res.isConfirmed) {
                              quitQueue();
                            }
                          })
                        }
                      />
                    )}
                    {queueConfig && queueConfig.useMenu && (
                      <MainButton
                        color={
                          config?.webappcolor.fontbutton
                            ? config.webappcolor.fontbutton
                            : "#636363"
                        }
                        backgroundColor={
                          config?.webappcolor.webappbutton
                            ? config.webappcolor.webappbutton
                            : "#EAEAEA"
                        }
                        text="Ver cardápio"
                        onClick={navigateToMenu}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
